/*  Desktop > Production > Production Records */
import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonGroup, Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FilterDateTime, FilterSelect, ItemDropdownMenu, Pager, ServiceThreadSpinner, SortHeader } from ".";
import { ConfirmModal, ItemTypeahead, ProdRecordEditModal, SpinnerButton } from './';
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from "../utils";
import _ from "lodash";

export default function ProductionSearch() {
   const { currentUser } = useContext(UserContext);
   const BLANK_OPTION = { value: "", label: "-- Select --" };
   const SELECT_OPTION = { value: 0, label: "-- Select --" };
   const ALL_OPTION = { value: 0, label: "N/A" };
   const ALL_MACHINES = {
      value: 0,
      label: "-- Select --",
      activeMachines: "0",
      spindles: 0,
      sides: false,
   };
   const INIT_MODEL = {
      DefectId: 0,
      DeptId: 0,
      FromDate: '2024-01-01', // Feature did not exist until 2024
      Item: "",
      MachineId: 0,
      MachineNum: 0,
      PageNumber: 1,
      PageSize: 100,          //Default # of rows in a page
      RecordType: 0,
      Shift: "",
      SortDirection: "desc",
      SortField: "DateCompleted",
      Spindle: 0,
      ToDate: '',
      UserId: 0
   };
   const [filters, setFilters] = useState([
      { filterName: "defectId", value: SELECT_OPTION },
      { filterName: 'deptId', value: SELECT_OPTION },
      { filterName: "machineId", value: ALL_MACHINES },
      { filterName: "machineNum", value: ALL_OPTION },
      { filterName: 'shift', value: BLANK_OPTION },
      { filterName: "spindle", value: ALL_OPTION },
      { filterName: 'userId', value: SELECT_OPTION },
      { filterName: 'item', value: null },
      { filterName: "StartDate", value: "" },
      { filterName: "EndDate", value: "" }
   ]);
   const item = filterHelpers.getValue(filters, "item");
   const [confirmDelete, setConfirmDelete] = useState(false);
   // null until the first search returns: the page/sort effect below skips on null, so it cannot fire a search on
   // mount before the date defaults are in place (it did, as "" - and posted an empty ToDate, an HTTP 400).
   const [data, setData] = useState(null);             // Grid data results
   const [defects, setDefects] = useState([]);         // Defect types for dropdown
   const [deleteId, setDeleteId] = useState(0);        // Id of record to be deleted
   const [depts, setDepts] = useState([]);             // Departments for dropdown
   const [editId, setEditId] = useState(0);            // Id of record to be edited
   const [editItem, setEditItem] = useState(null);     // Record to be edited
   const [end, setEnd] = useState("");                 // End Date filter
   const [footerWgt, setWgt] = useState(0);            // Total Pkg Weight across all records, not just the page
   const [initialized, setInit] = useState(false);     // Has screen been initialized with drop-downs?
   const [loading, setLoading] = useState(false);      // Show or hide spinner on spinner buttons
   const [machines, setMachines] = useState([]);       // Active machine types 
   const [machineNums, setMachineNums] = useState([]); // Active machines for selected machine type
   const [model, setModel] = useState(INIT_MODEL);     // Search Criteria for payload
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(100);      // Default rows in grid page is 100
   const [rcdType, setRcdType] = useState(0);          // 0=All, 1=Good, 2=Defects
   const [rptOpt, setRptOpt] = useState(1);            // Operator efficiency reports selected date range option
   const [shifts, setShifts] = useState([]);           // Shifts for dropdown
   const [showEditModal, setShowEditModal] = useState(false);
   const [sortDirection, setSortDirection] = useState("desc");
   const [sortField, setSortField] = useState("DateCompleted");
   const [spin, setSpinner] = useState(false);         // Shows or hides spinner component
   const [spindles, setSpindles] = useState([]);       // Spindles available for selected machine
   const [totalPages, setTotalPages] = useState();
   const [users, setUsers] = useState([]);             // Record Users for dropdown

   useEffect(() => {
      if (!currentUser) return;
      setEnd(getDefaultStartDate());
      api
         .fetch(`Production/InitConeLists`)
         .then((response) => {
            if (response.data.success) {
               const machineList = _.concat(
                  [ALL_MACHINES],
                  _.map(response.data.machines, (m) => ({
                     value: m.machineId,
                     label: m.machineName,
                     activeMachines: m.activeMachines,
                     spindles: m.spindles,
                     sides: m.hasBSide,
                  }))
               );
               const defectList = _.concat(
                  [SELECT_OPTION],
                  _.map(response.data.defects, (d) => ({
                     value: d.id,
                     label: d.defect,
                  }))
               );
               const deptList = _.concat(
                  [SELECT_OPTION],
                  _.map(response.data.depts, (dpt) => ({
                     value: dpt.deptId,
                     label: dpt.deptName,
                  }))
               );
               const shiftList = _.concat(
                  [BLANK_OPTION],
                  _.map(response.data.shifts, (sh) => ({
                     value: sh.id.toString(),
                     label: sh.name,
                  }))
               );
               const userList = _.concat(
                  [SELECT_OPTION],
                  _.map(response.data.users, (usr) => ({
                     value: usr.id,
                     label: usr.name,
                  }))
               );
               return {
                  machineList: machineList,
                  defectList: defectList,
                  deptList: deptList,
                  shiftList: shiftList,
                  userList: userList
               };
            } else {
               throw new Error(response);
            }
         })
         .then(({ machineList, defectList, deptList, shiftList, userList }) => {
            setMachines(machineList);
            setDefects(defectList);
            setDepts(deptList);
            setShifts(shiftList);
            setUsers(userList);
            // These should only fill in after a machine is selected, just put in default selections
            setMachineNums([ALL_OPTION]);
            setSpindles([ALL_OPTION]);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
            setInit(true);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser]);

   useEffect(() => {
      if (deleteId > 0)
         setConfirmDelete(true);
      else
         setConfirmDelete(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [deleteId]); // Handle row delete request

   useEffect(() => {
      if (editId > 0) {
         api
            .fetch(`Production/GetProductionRecord/${editId}`)
            .then(r => {
               if (r.data.success) {
                  if (r.data.message !== null && r.data.message !== undefined) {
                     setEditItem(r.data.message);
                  }
               } else {
                  toast.error(r.data.message);
                  setEditItem(null);
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
               setEditItem(null);
            })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editId]);   // Handle row edit request

   useEffect(() => {
      setEditId(0);
      if (editItem)
         setShowEditModal(true);
      else
         setShowEditModal(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editItem]); // edit record

   useEffect(() => {
      onFilterChange({ filterName: "StartDate", value: end, filteredDisplay: '' })
      let changes = Object.assign({}, model);
      changes.FromDate = end;
      setModel(changes);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [end])

   useEffect(() => {
      if (!initialized) return;
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [model, initialized])

   useEffect(() => {
      if (data === null) return;
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [page, sortField, sortDirection]);

   function createPayload() {
      let payload = Object.assign({}, model);
      if (payload.FromDate === undefined || payload.FromDate === null || payload.FromDate === "")
         payload.FromDate = end;
      if (payload.ToDate === undefined || payload.ToDate === null || payload.ToDate === "")
         payload.ToDate = end;
      payload.ReportType = rptOpt;
      payload.PageNumber = page;
      payload.PageSize = pageSize;
      payload.SortDirection = sortDirection;
      payload.SortField = sortField;
      return payload;
   }

   function deleteRcd() {
      setConfirmDelete(false);
      setSpinner(true);
      api
         .post(`Production/DeleteProductionRecord/${deleteId}`)
         .then(r => {
            if (r.data.success) {
               toast.success("Production record successfully deleted.");
               runQuery();
            }
            else {
               toast.error(r.data.message);
               setSpinner(false);
            }
         })
         .catch(api.catchHandler)
         .finally(setDeleteId(0));
   }

   function exportGrid() {
      setLoading(true);
      api
         .postGetBlob("Production/ExportConeRecords", createPayload())
         .then(response => {
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = `Production_Records.xlsx`
                  helpers.downloadExport(response.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
               }
            } else {
               // postGetBlob resolves a failure as { data: false, message } - show the server's text.
               toast.error(response?.message || "There was an error downloading the file");
            }
         }).catch((error) => {
            api.catchHandler(error)
            toast.error("There was an error downloading the file");
         })
         .finally(() => setLoading(false));
   }

   const fixMachines = async (activeMachines) => {
      // Fill in the drop-down with active machines for the selected machine
      var nums = activeMachines.split(", ");
      let newMachineList = _.concat(
         [ALL_OPTION],
         _.map(nums, (m) => ({ value: parseInt(m, 10), label: m }))
      );
      setMachineNums(newMachineList);
      return newMachineList;
   };

   const fixSpindles = async (spindlesPerSide) => {
      // Fill in the drop-down with available spindles for the selected machine
      let newSpindleList = [ALL_OPTION];
      for (let i = 1; i <= spindlesPerSide; i++) {
         newSpindleList = _.concat(
            newSpindleList,
            _.map([{ value: i, label: i.toString() }])
         );
      }
      setSpindles(newSpindleList);
      return newSpindleList;
   };

   function getDefaultStartDate() {
      let newDate = new Date();
      let date = newDate.getDate();
      let month = newDate.getMonth() + 1;
      let year = newDate.getFullYear();
      let tmpDate = `${year}-${month < 10 ? `0${month}` : `${month}`}-${date < 10 ? `0${date}` : `${date}`}`;
      return tmpDate;
   }

   function onDefectChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.DefectId = newFilterValue.value;
      setModel(changes);
   }

   function onDeptChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.DeptId = newFilterValue.value;
      setModel(changes);
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
   }

   function onFromChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.FromDate = (newFilterValue?.value === undefined || newFilterValue?.value === null) ? "" : newFilterValue.value;
      setModel(changes);
   }

   function onItemChange(opt) {
      onFilterChange({ filterName: "item", value: opt, filteredDisplay: '' })
      let changes = Object.assign({}, model);
      if (opt === undefined || opt === null)
         changes.Item = "";
      else
         changes.Item = opt.itemNumber;
      setModel(changes);
   }

   const onMachineChange = async (newFilterValue) => {
      // Fill in the machine related drop-down lists with proper values
      const mach = _.find(machines, (x) => x.value === newFilterValue.value);
      let newMachList = await fixMachines(mach.activeMachines);
      let newSpindleList = await fixSpindles(mach.spindles);

      // Set the machine dependent filters to their defaults
      const numFilter = _.find(newMachList, (m) => m.value === 0);
      const spindleFilter = _.find(newSpindleList, (s) => s.value === 0);

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "machineId",
         value: mach,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "machineNum",
         value: numFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "spindle",
         value: spindleFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, model);
      changes.MachineId = newFilterValue.value;
      changes.MachineNum = ALL_OPTION.value;
      changes.Spindle = ALL_OPTION.value;
      setModel(changes);
   };

   function onMachineNumChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.MachineNum = newFilterValue.value;
      setModel(changes);
   }

   function onPageChange(page) {
      setPage(page);
   }

   function onPageSizeChange(size) {
      setPageSize(size);
      setTotalPages(null);
      onPageChange(1);
   }

   function onShiftChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.Shift = newFilterValue.value;
      setModel(changes);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
   }

   function onSpindleChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.Spindle = newFilterValue.value;
      setModel(changes);
   }

   function onToChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.ToDate = (newFilterValue?.value === undefined || newFilterValue?.value === null) ? "" : newFilterValue.value;
      setModel(changes);
   }

   function onTypeChange(opt) {
      setRcdType(opt);
      let changes = Object.assign({}, model);
      changes.RecordType = opt;
      setModel(changes);
   }

   function onUserChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.UserId = newFilterValue.value;
      setModel(changes);
   }

   function printOperatorReport() {
      setLoading(true);
      var pay = createPayload();

      if (rptOpt === 0) {
         var tmpDate = dateHelpers.formatDateForServer(dateHelpers.addDays(pay.FromDate, 7));
         if (pay.ToDate === undefined || pay.ToDate === null || pay.ToDate === "") {
            pay.ToDate = dateHelpers.formatDateForServer(dateHelpers.currentDate());            
         }         
         if (pay.ToDate > tmpDate) {
            toast.warning("A maximum of 7 days is allowed on this report");
            setLoading(false);
            return;
         }
      }

      var rptType = "24_Hour";
      if (pay.Shift === "1") rptType = "1st_Shift";
      else if (pay.Shift === "2") rptType = "2nd_Shift";
      else if (pay.Shift === "3") rptType = "3rd_Shift";

      api
         .postGetBlob("Production/OperatorEfficiencyReport", pay)
         .then(response => {
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = `${rptType}_Efficiency_Report.xlsx`
                  helpers.downloadExport(response.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
               }
            } else {
               // postGetBlob resolves a failure as { data: false, message } - show the server's text.
               toast.error(response?.message || "There was an error downloading the file");
            }
         }).catch((error) => {
            api.catchHandler(error)
            toast.error("There was an error downloading the file");
         })
         .finally(() => setLoading(false));
   }

   function printSummaryReport() {
      setLoading(true);
      api
         .postGetBlob("Production/ConeFrequencyReport", createPayload())
         .then(response => {
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = `Production_Frequency.xlsx`
                  helpers.downloadExport(response.data, XLSX_FILENAME, constants.MIME_TYPES.xlsx)
               }
            } else {
               // postGetBlob resolves a failure as { data: false, message } - show the server's text.
               toast.error(response?.message || "There was an error downloading the file");
            }
         }).catch((error) => {
            api.catchHandler(error)
            toast.error("There was an error downloading the file");
         })
         .finally(() => setLoading(false));
   }

   function runQuery() {
      let pay = createPayload();
      setLoading(true);
      setSpinner(true);
      api
         .post(`Production/PaginatedConeSearch`, pay)
         .then(response => {
            if (response.data.success) {
               setData(response.data.cones.items);
               setTotalPages(_.ceil(response.data.cones.totalCount / pageSize));
               setWgt(response.data.totWgt);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
         })
         .finally(() => {
            setLoading(false)
            setSpinner(false);
         });
   }

   return (
      <DesktopContainer screenName="Search Production Records">
         <div className="filter-header">
            <Row className="justify-content-right">
               <Col xs={6}>
                  {/* Start Date filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Start Date:</Col>
                     <Col xs={8}>
                        <FilterDateTime
                           filterName="StartDate"
                           value={filterHelpers.getValue(filters, "StartDate") || ""}
                           onChangeCallback={e => onFromChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* End Date filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>End Date:</Col>
                     <Col xs={8}>
                        <FilterDateTime
                           filterName="EndDate"
                           value={filterHelpers.getValue(filters, "EndDate") || ""}
                           onChangeCallback={e => onToChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* User filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Operators:</Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="userId"
                           options={users}
                           value={filterHelpers.getValue(filters, "userId")}
                           onChangeCallback={(e) => onUserChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* Defect filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Defect:</Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="defectId"
                           options={defects}
                           value={filterHelpers.getValue(filters, "defectId")}
                           onChangeCallback={(e) => onDefectChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* Item filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Item:</Col>
                     <Col xs={8}>
                        <ItemTypeahead
                           name="itemInput"
                           value={item}
                           onChange={onItemChange}
                        />
                     </Col>
                  </Row>
               </Col>


               <Col xs={6}>
                  {/* Shift filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Shift:</Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="shift"
                           options={shifts}
                           value={filterHelpers.getValue(filters, "shift")}
                           onChangeCallback={(e) => onShiftChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* Department filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Dept:</Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="deptId"
                           options={depts}
                           value={filterHelpers.getValue(filters, "deptId")}
                           onChangeCallback={(e) => onDeptChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* Machine type filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Machine:</Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="machineId"
                           options={machines}
                           value={filterHelpers.getValue(filters, "machineId")}
                           onChangeCallback={(e) => onMachineChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* Machine # filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Machine #:</Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="machineNum"
                           options={machineNums}
                           value={filterHelpers.getValue(filters, "machineNum")}
                           onChangeCallback={(e) => onMachineNumChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* Spindle filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>Spindle #:</Col>
                     <Col xs={8}>
                        <FilterSelect
                           filterName="spindle"
                           options={spindles}
                           value={filterHelpers.getValue(filters, "spindle")}
                           onChangeCallback={(e) => onSpindleChange(e)}
                        />
                     </Col>
                  </Row>

                  {/* Defects, Good, or All filter */}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={3}>&nbsp;</Col>
                     <Col xs={8}>
                        <FormGroup check inline>
                           <Label check className="text-white">
                              <Input
                                 type="radio"
                                 name="radio1"
                                 checked={rcdType === 0}
                                 onChange={e => onTypeChange(0)}
                              /> All
                           </Label>
                        </FormGroup>
                        <FormGroup check inline>
                           <Label check className="text-white">
                              <Input
                                 type="radio"
                                 name="radio1"
                                 checked={rcdType === 1}
                                 onChange={e => onTypeChange(1)}
                              /> Good
                           </Label>
                        </FormGroup>
                        <FormGroup check inline>
                           <Label check className="text-white">
                              <Input
                                 type="radio"
                                 name="radio1"
                                 checked={rcdType === 2}
                                 onChange={e => onTypeChange(2)}
                              /> Defects
                           </Label>
                        </FormGroup>
                     </Col>
                  </Row>

                  {/* Query Button
                  <Row className="align-items-center my-1">
                     <Col className="ustify-content-right h6 fw-bold my-0" xs={3}>&nbsp;</Col>
                     <Col xs={8}>
                        <SpinnerButton
                           title="Load"
                           className="shredButton btn-md w-100"
                           onClick={() => runQuery()}
                           on={loading}
                           disabled={loading}
                        > Load Results
                        </SpinnerButton>
                     </Col>
                  </Row> */}
               </Col>
            </Row>

            {/* Print Efficiency Report Options */}
            <hr />
            <Row className="justify-content-center">
               <Col xs={11}>
                  <div className="float-end">
                     <FormGroup check inline>
                        <Label check className="text-white">
                           <Input
                              type="radio"
                              name="radio2"
                              checked={rptOpt === 0}
                              onChange={e => setRptOpt(0)}
                           /> Selected Date Range
                        </Label>
                     </FormGroup>
                     <FormGroup check inline>
                        <Label check className="text-white">
                           <Input
                              type="radio"
                              name="radio2"
                              checked={rptOpt === 1}
                              onChange={e => setRptOpt(1)}
                           /> Yesterday
                        </Label>
                     </FormGroup>
                     <FormGroup check inline>
                        <Label check className="text-white">
                           <Input
                              type="radio"
                              name="radio2"
                              checked={rptOpt === 2}
                              onChange={e => setRptOpt(2)}
                           /> This Week
                        </Label>
                     </FormGroup>
                     <FormGroup check inline>
                        <Label check className="text-white">
                           <Input
                              type="radio"
                              name="radio2"
                              checked={rptOpt === 3}
                              onChange={e => setRptOpt(3)}
                           /> Last Week
                        </Label>
                     </FormGroup>
                     <ButtonGroup className="inline">
                        <SpinnerButton
                           className="shredButton mx-1"
                           title="Operator Report"
                           onClick={printOperatorReport}
                           on={loading}
                           disabled={loading}
                        >Print Efficiency
                        </SpinnerButton>
                     </ButtonGroup>
                  </div>
               </Col>
            </Row>
         </div>

         <ServiceThreadSpinner on={spin}>
            <br />
            {data && data.length ? (
               <Row className="px-5">
                  <Row className="align-items-center my-1">
                     <Col>
                     </Col>
                  </Row>

                  <Row className="align-items-center mb-1">
                     <Col className="transparent-background d-flex">
                        <Pager
                           callBack={onPageChange}
                           sizeCallback={onPageSizeChange}
                           pageNumber={page}
                           pageSize={pageSize}
                           totalPages={totalPages}
                           downloading={loading}
                           defaultPageSize={100}
                           allowAll
                        />
                     </Col>
                     <Col className="transparent-background">
                        <ButtonGroup className="float-end">
                           <SpinnerButton
                              className="shredButton mx-1"
                              title="Summary Report"
                              onClick={printSummaryReport}
                              on={loading}
                              disabled={loading}
                           >Print Summary
                           </SpinnerButton>

                           <SpinnerButton
                              title="Download as .xlsx"
                              className="shredButtonExcel mx-1"
                              onClick={exportGrid}
                              on={loading}
                              disabled={loading}
                           ><FontAwesomeIcon icon="file-excel" size="lg" />
                           </SpinnerButton>
                        </ButtonGroup>
                     </Col>
                  </Row>
                  <Table striped className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th className={"w-10"}>
                              <SortHeader
                                 displayName="Completed"
                                 field="DateCompleted"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "DateCompleted"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th className={"w-10"}>
                              <SortHeader
                                 displayName="Cone #"
                                 field="ConeId"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "ConeId"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Item"
                                 field="Item"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "Item"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Dept"
                                 field="Department"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "Department"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Machine"
                                 field="MachineName"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "MachineName"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Machine #"
                                 field="MachineNum"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "MachineNum"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Side"
                                 field="Side"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "Side"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Spindle"
                                 field="Spindle"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "Spindle"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="State"
                                 field="Defect"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "Defect"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Pkg Wt"
                                 field="PkgWeight"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "PkgWeight"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="User"
                                 field="UserName"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "UserName"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Shift"
                                 field="ShiftDesc"
                                 sortDirection={sortDirection}
                                 sorted={sortField === "ShiftDesc"}
                                 callback={onSortChange}
                              />
                           </th>
                           {currentUser?.isAdmin ? (
                              <th></th>
                           ) : null}
                        </tr>
                     </thead>

                     <tbody>
                        {_.map(data, (datum, index) => {
                           return (
                              <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                 <td className="data-cell" children={dateHelpers.dateTimeFormat(datum.dateCompleted, "MM-DD-YYYY HH:mm")} />
                                 <td className="data-cell" children={datum.coneId} />
                                 <ItemDropdownMenu
                                    itemNumber={datum.item}
                                    itemDescription={datum.itemWithDesc}
                                    attachmentCount={datum.itemAttachmentCount}
                                 />
                                 <td className="data-cell" children={datum.department} />
                                 <td className="data-cell" children={datum.machineName} />
                                 <td className="data-cell" children={datum.machineNum} />
                                 <td className="data-cell" children={datum.side ? datum.side : ""} />
                                 <td className="data-cell" children={datum.spindle} />
                                 <td className="data-cell" children={datum.defect} />
                                 <td className="data-cell" children={datum.pkgWeight} />
                                 <td className="data-cell" children={datum.userName} />
                                 <td className="data-cell" children={datum.shiftDesc} />
                                 {currentUser?.isAdmin ? (
                                    <td>
                                       <Button className="icon-button me-1"
                                          title="Edit"
                                          color="info"
                                          onClick={() => setEditId(datum.id)}
                                          disabled={spin}
                                       >
                                          <FontAwesomeIcon className="whiteIcon" icon={"edit"} />
                                       </Button>

                                       <Button className="icon-button"
                                          title="Delete"
                                          color="danger"
                                          onClick={() => { setDeleteId(datum.id); }}
                                          disabled={spin}
                                       >
                                          <FontAwesomeIcon className="whiteIcon" icon="trash" />
                                       </Button>
                                    </td>
                                 ) : null}
                              </tr>
                           )
                        })}
                     </tbody>

                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan={8} />
                           <td className="text-bold">Total Pkg Wt:</td>
                           <td className="text-bold">{helpers.formatNumberWithThousands(footerWgt)}</td>
                           {currentUser?.isAdmin ? (<td colSpan={3} />) : <td colSpan={2} />}
                        </tr>
                     </tfoot>
                  </Table>
               </Row>
            )
               : <div className="h5 text-center mt-3">No data found</div>}

            <ConfirmModal
               confirmDelete={confirmDelete}
               approveConfirmDelete={() => deleteRcd()}
               declineConfirmDelete={() => setDeleteId(0)}
               header={`Please Confirm`}
               warningMessage={
                  <>
                     Are you sure you want to delete this production record?
                  </>
               }
            />

            {showEditModal &&
               <ProdRecordEditModal
                  editItem={editItem}
                  toggleCallback={() => {
                     setEditItem(null);
                  }}
                  callbackRefresh={() => {
                     setEditItem(null);
                     runQuery();
                  }}
               />
            }
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
